/**
 * Feedback keyboard-shortcut helpers (#1979, updated #2008; D4-FBK-032).
 *
 * The global launch shortcut for the feedback / brightmark annotate flow is
 * **Ctrl + Shift + F** on Windows/Linux and **Cmd + Shift + F** (⌘⇧F) on macOS
 * ("F" for Feedback). It deliberately avoids the existing global search
 * ``Cmd/Ctrl + K`` (GlobalSearchProvider). ``Cmd/Ctrl + Shift + F`` is not a
 * strong browser default (unlike the bookmark-bar clash that ruled out
 * ``Cmd/Ctrl + Shift + B``) and shadows no in-app keydown handler.
 *
 * These pure predicates are split out so they unit-test in isolation and are
 * shared by FeedbackButton's window-level keydown handler.
 */

/**
 * True when ``el`` is a text-entry surface where the shortcut must stay INERT
 * (don't hijack the key mid-typing): ``<input>`` / ``<textarea>`` /
 * ``[contenteditable]`` (or ``isContentEditable``) / ``[role="textbox"]``.
 */
export function isTypingTarget(el: EventTarget | null): boolean {
  if (!el || !(el instanceof HTMLElement)) return false;
  const tag = el.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if (el.isContentEditable) return true;
  if (el.getAttribute("contenteditable") === "true") return true;
  if (el.getAttribute("role") === "textbox") return true;
  return false;
}

/**
 * True when the keydown is the feedback launch combo: the platform's primary
 * modifier (Cmd on macOS, Ctrl elsewhere) + Shift + F.
 *
 * Matches on ``e.code === "KeyF"`` (physical key) rather than ``e.key`` because
 * a held modifier on some layouts can rewrite ``e.key``; the physical-key check
 * is layout-robust. The OPPOSITE primary modifier must NOT be held (so the Mac
 * combo never fires on a held Ctrl and vice versa, keeping each platform's
 * single intended chord) and ``Alt`` must NOT be held (the old ``Alt+Shift+F``
 * is no longer bound).
 *
 * ``platform`` defaults to ``navigator`` UA-sniffing so callers can pass a fixed
 * value in tests; ``true`` ⇒ treat as macOS (require ``metaKey``), ``false`` ⇒
 * Windows/Linux (require ``ctrlKey``).
 */
export function matchesFeedbackShortcut(e: KeyboardEvent, isMac: boolean = detectMac()): boolean {
  if (!e.shiftKey || e.altKey) return false;
  const primary = isMac ? e.metaKey && !e.ctrlKey : e.ctrlKey && !e.metaKey;
  if (!primary) return false;
  return e.code === "KeyF" || e.key === "F" || e.key === "f";
}

/** UA-sniff for macOS (mirrors FeedbackButton's own platform detection). */
export function detectMac(): boolean {
  if (typeof navigator === "undefined") return false;
  return /Mac|iPhone|iPad|iPod/i.test(navigator.platform || navigator.userAgent);
}
